import type { LoaderFunctionArgs } from '@remix-run/node'
import { z } from 'zod'
import { zx } from 'zodix'
import { getDemoSession } from '~/services/contentful'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { renderOption } from '~/utils/contentful'
import { H1, H2, H3, P, Stack } from '~/components-v2'
import { H1_LINE_HEIGHT } from '~/components-v2/text/types'
import { rudderStack } from '~/utils/rudder-stack.server'
import { typedjson, useTypedLoaderData } from 'remix-typedjson'
import { editorialDate } from '~/utils/date'
import { VideoFigure } from '~/components-v2/figure/video-figure'
import { Demo } from '~/types'
import { Author } from '~/components'
import { startCase } from 'lodash-es'

export { meta } from '~/utils'

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const { slug } = zx.parseParams(params, { slug: z.string().optional() })
  const demoSession = await getDemoSession(slug)
  rudderStack(request, demoSession.title)
  return typedjson({
    ...demoSession,
    sectionTitle: 'Demos',
    swiftypeSiteType: 'Demos',
    title: `${editorialDate(demoSession.date)} - ${demoSession.title}`,
    description: demoSession.title,
  })
}

export default function () {
  const { description, date, demos, summary } =
    useTypedLoaderData<typeof loader>()
  return (
    <article className="demo-session">
      <Stack as="hgroup" gap={2}>
        <H1
          lineHeight={H1_LINE_HEIGHT}
          fontSize="xxxl"
          wrap="balance"
          fontWeight={600}
        >
          {description}
        </H1>
        <H2 fontSize="md" color="text4">
          {editorialDate(date)}
        </H2>
      </Stack>
      <Stack className="editorial-copy" padding="0,0,6">
        {documentToReactComponents(summary, renderOption)}
      </Stack>
      <Stack gap={10}>
        {demos.map((demo) => (
          <DemoItem key={demo.slug} {...demo} />
        ))}
      </Stack>
    </article>
  )
}

const DemoBadge = ({ children }: { children?: string }) =>
  children ? <span className="demo-badge">{children}</span> : null

const DemoItem = ({
  videoUrl,
  description,
  title,
  presenters,
  labels,
  slug,
  status,
}: Demo) => {
  return (
    <Stack gap={2} className="demo" id={slug}>
      {videoUrl && <VideoFigure src={videoUrl} title={title} />}
      <Stack align="center" as="hgroup" gap={3} row>
        <a href={`#${slug}`}>
          <H3 fontWeight={500} fontSize="md">
            {title}
          </H3>
        </a>
        <DemoBadge>{status}</DemoBadge>
      </Stack>
      <div className="editorial-copy">
        {documentToReactComponents(description, renderOption)}
      </div>
      <P color="text4" fontSize="xs" className="meta">
        {presenters.map((person) => (
          <Author gap={1.5} key={person.slug} {...person} />
        ))}
        <span>•</span>
        <span>{humanizeLabels(labels)}</span>
      </P>
    </Stack>
  )
}

const humanizeLabels = (labels: string[]) =>
  labels.map((label) => startCase(label.replace('-', ' '))).join(', ')
